import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'

import { FALLBACK_LANGUAGE } from '@app/i18n'

import { getLocaleFromPath, localizeUrl, type SupportedLocale } from './utils'


interface LocaleContextValue {
  locale: SupportedLocale
  setLocale: (locale: SupportedLocale) => void
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

interface LocaleProviderProps {
  children: ReactNode
}

export function LocaleProvider ({ children }: LocaleProviderProps) {
  const { i18n } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()

  // Extract locale from current URL path
  const urlLocale = useMemo(
    () => getLocaleFromPath(location.pathname),
    [ location.pathname ],
  )

  // Current effective locale
  const locale: SupportedLocale = urlLocale ?? (FALLBACK_LANGUAGE as SupportedLocale)

  // Sync i18n with URL locale
  useEffect(() => {
    if (locale && i18n.language !== locale) {
      i18n.changeLanguage(locale)
    }
  }, [ locale, i18n ])

  // Redirect to localized URL if no locale in path (except home page)
  useEffect(() => {
    // Skip redirect for home page - it works without locale
    if (location.pathname === '/') {
      return
    }
    
    if (!urlLocale) {
      const currentLang = (i18n.language || FALLBACK_LANGUAGE) as SupportedLocale
      const localizedPath = localizeUrl(
        location.pathname + location.search + location.hash,
        currentLang,
      )
      navigate(localizedPath, { replace: true })
    }
  }, [ urlLocale, i18n.language, location.pathname, location.search, location.hash, navigate ])

  // Change locale and navigate to new URL
  const setLocale = useCallback((newLocale: SupportedLocale) => {
    if (newLocale !== locale) {
      const newPath = localizeUrl(
        location.pathname + location.search + location.hash,
        newLocale,
      )
      i18n.changeLanguage(newLocale)
      navigate(newPath)
    }
  }, [ locale, location.pathname, location.search, location.hash, i18n, navigate ])

  const value = useMemo(() => ({ locale, setLocale }), [ locale, setLocale ])

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  )
}

export function useLocale () {
  const context = useContext(LocaleContext)

  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider')
  }

  return context
}
